import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { signup, signupCaptchaFailure } from '../../../actions/signup-actions';
import { useAppDispatch, useAppSelector } from '../../../stores/redux.store';
import { useAnalyticsClient } from '../../../utilities/analytics/analytics-web-client';
import { getEmailFromLoginHint, useLoginHint } from '../../../utilities/hooks/login-hint';
import { SignupFormData } from '../components/SignupForm';
import { pageId } from '../constants';
import { useSignupPageContext } from '../contexts/SignupPageContext';
import { useRegisterComponent } from '../../orchestrator';
import { useRecaptchaContext } from '../../../contexts/recaptcha';
import { SignupFieldError } from '../../../types';
import { useTranslateFormErrorIfAvailable } from '../FormValidation.messages';
import { captureException } from '../../../utilities/analytics/error-reporting';

export type SignupFieldErrorsWithMessages = {
  // TODO [KIRBY-7532]: Remove the `undefined` type from the `code` field after removing useTranslateFormErrorIfAvailable
  [K in keyof SignupFieldError]: { code: string | undefined; message: string };
};

const useEmailFromSearchParams = () => {
  const loginHint = useLoginHint();
  const [queryParams] = useSearchParams();

  const email = getEmailFromLoginHint(loginHint) || queryParams.get('email');
  const shouldDisableEmailInput = queryParams.get('disableEmailInput') === 'true';

  return {
    email,
    shouldDisableEmailInput,
  };
};

type UseSignupSubmitOptions = {
  /**
   * The server may indicate that the signup form ought to be auto-submitted immediately, with
   * the initial email value.
   *
   * This option provides a way to opt out of this default behaviour.
   * */
  autoSubmit?: boolean;
};

type OnSignupOptions = {
  /**
   * Callback that returns search params that will be added to the continue URL, itself a param
   * of the full redirect URL that the client will go to after a successful onSignup call.
   *
   * Consumers may want to programmatically include additional params inside of this continue URL,
   * to carry information to the destination product about what had happened during signup.
   *
   * To this end, the callback includes the full redirect URl and the continue URl as args, and
   * the consumer may use these to build additional search params for the continue URL.
   *
   * For example, the signup may have redirected to /login if the account already exists. In this
   * case, the consumer may parse the full redirect URl to see that the `pathname` was `/login`, and
   * use this to include a param like `source=idac-login` in the continue URL.
   */
  addParamsToContinueUrl?: (_: { fullRedirectUrl: URL; continueUrl: URL }) => URLSearchParams;
};

export const useSignupSubmit = ({
  autoSubmit: autoSubmitOption = true,
}: UseSignupSubmitOptions = {}) => {
  useRegisterComponent(useSignupSubmit.name);
  const dispatch = useAppDispatch();
  const analyticsClient = useAnalyticsClient();

  const [hasAutoSubmitted, setHasAutoSubmitted] = useState(false);

  const { privacyPolicyText = '' } = useSignupPageContext();

  const [_, setQueryParams] = useSearchParams();

  const { fetchToken, recaptchaType } = useRecaptchaContext();

  const fieldErrors = useAppSelector(state => state.signup?.fieldErrors);

  const autoSubmitDefault = useAppSelector(state => state.signup?.autoSubmit);

  const autoSubmit = useMemo(
    () => autoSubmitDefault && autoSubmitOption,
    [autoSubmitDefault, autoSubmitOption]
  );

  const onSignup = useCallback(
    async (signupData: SignupFormData, options?: OnSignupOptions) => {
      try {
        const challengeResponse = await fetchToken();

        setQueryParams(urlParams => {
          urlParams.delete('errorCode');
          if (urlParams.has('email')) urlParams.delete('email');
          urlParams.append('email', signupData.email);
          return urlParams;
        });

        analyticsClient.formSubmittedEvent(pageId, 'signupForm', { autoSubmit, recaptchaType });

        return dispatch(
          signup(
            {
              ...signupData,
              challengeResponse,
              privacyPolicyText,
            },
            undefined,
            options
          )
        );
      } catch (e) {
        captureException(e);
        dispatch(signupCaptchaFailure(navigator.onLine));
      }
    },
    [
      setQueryParams,
      analyticsClient,
      dispatch,
      fetchToken,
      recaptchaType,
      privacyPolicyText,
      autoSubmit,
    ]
  );

  const { email, shouldDisableEmailInput } = useEmailFromSearchParams();

  useEffect(() => {
    if (autoSubmit && email && !hasAutoSubmitted) {
      onSignup({ email });
      setHasAutoSubmitted(true);
    }
  }, [autoSubmit, email, onSignup, hasAutoSubmitted, setHasAutoSubmitted]);

  const emailTranslation = useTranslateFormErrorIfAvailable(fieldErrors.email);
  const fieldErrorsWithTranslations = useCallback(
    (fieldErrors: SignupFieldError): SignupFieldErrorsWithMessages => {
      // fieldErrors.email could either be a code or a pre-translated message.
      const emailErrorIsCode = fieldErrors.email !== emailTranslation;
      return {
        ...(fieldErrors.email && {
          email: {
            code: emailErrorIsCode ? fieldErrors.email : undefined,
            // TODO [KIRBY-7532]: remove fallback to empty string after removing useTranslateFormErrorIfAvailable.
            //   emailTranslation will always be defined if fieldErrors.email is defined.
            message: emailTranslation || '',
          },
        }),
      };
    },
    [emailTranslation]
  );

  return {
    onSignup,
    fieldErrors: fieldErrorsWithTranslations(fieldErrors),
    email,
    shouldDisableEmailInput,
  };
};
